/**
 * Enhanced Chat API Service with JWT Authentication
 * 
 * Handles all API communication with the SpeedGauge FastAPI server.
 * Provides methods for session management and chat messaging with automatic
 * JWT token inclusion and refresh handling.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { logger } from '../utils/logger';
import { API_BASE_URL } from '../utils/config';
import type {
  SessionCreateRequest,
  SessionCreateResponse,
  ChatRequest,
  ChatResponse,
  HealthResponse,
  ErrorResponse,
  FeedbackRequest,
  FeedbackResponse,
  SessionHistoryResponse,
  SessionMessagesResponse,
  SessionResumeResponse,
  APIClient
} from '../types';
import { categorizeError } from '../utils/errorHandler';
import type { StreamedSessionDoc } from '../utils/sessionDocStreamEvent';
import {
  getBrowserAuthHeaders,
  notifyBrowserAuthUnauthorized,
  refreshBrowserAuthSession,
} from './browserAuthTransport';

// Token management interface
interface TokenManager {
  getToken(): string | null;
  refreshToken(): Promise<string | null>;
  clearTokens(): void;
}

const moduleLogger = logger.getModule('ChatService');

interface StreamCallbacks {
  onStatus?: (status: string) => void;
  onContent?: (chunk: string) => void;
  onUpdate?: (chunk: string, payload?: Record<string, any>) => void;
  onResponse?: (payload?: Record<string, any>) => void;
  onToolCall?: (name: string, input?: Record<string, any>) => void;
  onToolUse?: (name: string, input?: Record<string, any>, payload?: Record<string, any>) => void;
  onToolResult?: (name: string, geoData?: Record<string, any>, payload?: Record<string, any>) => void;
  onReasoning?: (message: string) => void;
  onSessionDoc?: (payload: StreamedSessionDoc) => void;
  onComplete?: (payload?: Record<string, any>) => void;
  onError?: (error: string) => void;
}

class EnhancedChatService implements APIClient {
  private apiClient: AxiosInstance;
  private tokenManager: TokenManager | null = null;
  private isRefreshingToken = false;
  private refreshPromise: Promise<string | null> | null = null;

  constructor(tokenManager?: TokenManager) {
    this.tokenManager = tokenManager || null;

    const apiTimeout = parseInt(process.env.REACT_APP_API_TIMEOUT || '120000', 10);

    moduleLogger.debug('Chat API configuration', { apiBaseUrl: API_BASE_URL, apiTimeout });
    
    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: apiTimeout,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    this.setupInterceptors();
  }

  /**
   * Set token manager for authentication
   */
  setTokenManager(tokenManager: TokenManager): void {
    this.tokenManager = tokenManager;
    moduleLogger.debug('Token manager configured for Chat Service');
  }

  /**
   * Remove token manager (for logout)
   */
  clearTokenManager(): void {
    this.tokenManager = null;
    this.isRefreshingToken = false;
    this.refreshPromise = null;
    moduleLogger.debug('Token manager cleared from Chat Service');
  }

  /**
   * Create a new chat session with authentication
   */
  async createSession(
    customerId: string,
    fleetId?: number | null,
    context?: any,
    returnHistory: boolean = true,
  ): Promise<SessionCreateResponse> {
    try {
      moduleLogger.debug('Creating authenticated session', { customerId, hasContext: !!context, returnHistory });
      
      const request: SessionCreateRequest = {
        customer_id: customerId,
        ...(typeof fleetId === 'number' ? { fleet_id: fleetId } : {}),
        return_fleets: true, // Always request fleets for header dropdown
        return_history: returnHistory, // Request history by default
        context
      };

      moduleLogger.debug('Session create request payload', { request });

      const response = await this.apiClient.post<SessionCreateResponse>('/sessions', request);
      const fleet_count = response.data.fleets ? response.data.fleets.length : 0;
      moduleLogger.debug('Authenticated session created', {
        sessionId: response.data.session_id,
        customerId: response.data.customer_id,
        customerName: response.data.customer_name,
        fleetCount: fleet_count
      });
      return response.data;
    } catch (error) {
      moduleLogger.error('Failed to create authenticated session', error);
      throw this.handleError(error);
    }
  }

  /**
   * Send a chat message with authentication
   */
  async sendMessage(sessionId: string, message: string, context?: any): Promise<ChatResponse> {
    try {
      moduleLogger.debug('Sending authenticated message', { sessionId });
      
      const request: ChatRequest = {
        message,
        context
      };

      const response = await this.apiClient.post<ChatResponse>(
        `/sessions/${sessionId}/chat`,
        request
      );

      moduleLogger.debug('Authenticated message sent, response received');
      return response.data;

    } catch (error) {
      moduleLogger.error('Failed to send authenticated message', error);
      throw this.handleError(error);
    }
  }

  /**
   * Send a chat message through the streaming endpoint (SSE).
   */
  async sendMessageStream(
    sessionId: string,
    message: string,
    context: any,
    callbacks: StreamCallbacks,
  ): Promise<void> {
    const token = this.tokenManager?.getToken();
    const endpoint = `${API_BASE_URL}/sessions/${sessionId}/chat`;
    const clientLocale = typeof navigator === 'undefined' ? null : navigator.language;
    const requestContext = {
      ...(context || {}),
      ...(clientLocale ? { clientLocale } : {}),
    };
    const response = await fetch(endpoint, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        ...getBrowserAuthHeaders('POST'),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ message, context: requestContext }),
    });

    if (!response.ok || !response.body) {
      if (response.status === 401) {
        notifyBrowserAuthUnauthorized();
      }
      const bodyText = await response.text().catch(() => '');
      throw new Error(`Streaming request failed (${response.status}): ${bodyText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let responseStarted = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n');
      buffer = events.pop() || '';

      for (const rawEvent of events) {
        const lines = rawEvent.split('\n');
        const eventLine = lines.find((line) => line.startsWith('event: '));
        const dataLine = lines.find((line) => line.startsWith('data: '));

        if (!dataLine) {
          continue;
        }

        const payloadText = dataLine.slice(6);
        let payload: any;
        try {
          payload = JSON.parse(payloadText);
        } catch {
          moduleLogger.warn('Failed to parse SSE payload', { payloadText });
          continue;
        }

        const eventType = String(payload.type || eventLine?.slice(7) || '').toLowerCase();
        if (eventType === 'status') {
          callbacks.onStatus?.(payload.status || 'processing');
        } else if (eventType === 'content' || eventType === 'update') {
          if (!responseStarted) {
            responseStarted = true;
            callbacks.onResponse?.(payload);
          }
          const chunk = payload.chunk || payload.content || '';
          callbacks.onContent?.(chunk);
          callbacks.onUpdate?.(chunk, payload);
        } else if (eventType === 'tool_call' || eventType === 'tool_use') {
          const toolName = payload.name || 'unknown';
          const toolInput = payload.input || {};
          callbacks.onToolCall?.(toolName, toolInput);
          callbacks.onToolUse?.(toolName, toolInput, payload);
        } else if (eventType === 'tool_result') {
          const toolName = payload.name || 'unknown';
          const geoData = payload.geo_data || {};
          callbacks.onToolResult?.(toolName, geoData, payload);
        } else if (eventType === 'reasoning') {
          callbacks.onReasoning?.(payload.message || '');
        } else if (eventType === 'session_doc') {
          callbacks.onSessionDoc?.(payload);
        } else if (eventType === 'response') {
          responseStarted = true;
          callbacks.onResponse?.(payload);
        } else if (eventType === 'error') {
          const errorMessage = payload.error || 'Unknown streaming error';
          callbacks.onError?.(errorMessage);
          throw new Error(errorMessage);
        } else if (eventType === 'complete') {
          callbacks.onComplete?.(payload);
        }
      }
    }
  }

  /**
   * Delete a chat session with authentication
   */
  async deleteSession(sessionId: string): Promise<void> {
    try {
      moduleLogger.debug('Deleting authenticated session', { sessionId });
      
      await this.apiClient.delete(`/sessions/${sessionId}`);
      moduleLogger.debug('Authenticated session deleted', { sessionId });

    } catch (error) {
      moduleLogger.error('Failed to delete authenticated session', error);
      throw this.handleError(error);
    }
  }

  /**
   * Keep a chat session alive by refreshing its activity timestamp.
   * 
   * This is a lightweight operation that prevents session timeout during
   * periods of inactivity (e.g., user is reading or thinking).
   */
  async keepSessionAlive(sessionId: string): Promise<{ expires_in_seconds: number }> {
    try {
      moduleLogger.debug('Refreshing session keep-alive', { sessionId });

      const response = await this.apiClient.post<{ 
        message: string;
        session_id: string;
        expires_in_seconds: number;
      }>(
        `/sessions/${sessionId}/keep-alive`,
        {}
      );

      moduleLogger.debug('Session keep-alive successful', {
        expiresInSeconds: response.data.expires_in_seconds
      });

      return {
        expires_in_seconds: response.data.expires_in_seconds
      };
    } catch (error) {
      moduleLogger.error('Failed to refresh session keep-alive', error);
      throw this.handleError(error);
    }
  }

  /**
   * Submit feedback for a chat message with authentication
   */
  async submitFeedback(
    messageId: number,
    activeSessionId: string,
    targetSessionId: string,
    rating: number,
    comments?: string,
  ): Promise<FeedbackResponse> {
    try {
      moduleLogger.debug('Submitting feedback', {
        messageId,
        activeSessionId,
        targetSessionId,
        rating,
      });
      
      const request: FeedbackRequest = {
        target_session_id: targetSessionId,
        rating,
        comments
      };

      const response = await this.apiClient.post<FeedbackResponse>(
        `/sessions/${activeSessionId}/messages/${messageId}/feedback`,
        request
      );

      moduleLogger.debug('Feedback submitted successfully');
      return response.data;

    } catch (error) {
      moduleLogger.error('Failed to submit feedback', error);
      throw this.handleError(error);
    }
  }

  /**
   * Check server health
   */
  async checkHealth(): Promise<HealthResponse> {
    try {
      const response = await this.apiClient.get<HealthResponse>('/health');
      moduleLogger.debug('Health check successful');
      return response.data;

    } catch (error) {
      moduleLogger.error('Health check failed', error);
      throw this.handleError(error);
    }
  }

  /**
   * Check if server is ready (includes dependency checks)
   */
  async checkReadiness(): Promise<HealthResponse> {
    try {
      const response = await this.apiClient.get<HealthResponse>('/health/ready');
      moduleLogger.debug('Readiness check successful');
      return response.data;

    } catch (error) {
      moduleLogger.error('Readiness check failed', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get paginated session history for the current user
   */
  async getSessionHistory(sessionId: string, page: number = 0, limit: number = 25): Promise<SessionHistoryResponse> {
    try {
      moduleLogger.debug('Fetching session history', { sessionId, page, limit });
      
      const response = await this.apiClient.get<SessionHistoryResponse>(
        `/sessions/${sessionId}/user/history`,
        { params: { page, limit } }
      );

      moduleLogger.debug('Session history retrieved', {
        count: response.data.sessions.length,
        total: response.data.total,
        hasMore: response.data.has_more
      });
      return response.data;

    } catch (error) {
      moduleLogger.error('Failed to fetch session history', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get messages for a specific session
   */
  async getSessionMessages(currentSessionId: string, targetSessionId: string): Promise<SessionMessagesResponse> {
    try {
      moduleLogger.debug('Fetching session messages', { currentSessionId, targetSessionId });
      
      const response = await this.apiClient.get<SessionMessagesResponse>(
        `/sessions/${currentSessionId}/content`,
        { params: { target_session_id: targetSessionId } }
      );

      moduleLogger.debug('Session messages retrieved', {
        sessionId: response.data.session_id,
        messageCount: response.data.messages.length
      });
      return response.data;

    } catch (error) {
      moduleLogger.error('Failed to fetch session messages', error);
      throw this.handleError(error);
    }
  }

  /**
   * Resume an existing active session and return its current content.
   */
  async resumeSession(): Promise<SessionResumeResponse> {
    try {
      moduleLogger.debug('Resuming pending handoff session');

      const response = await this.apiClient.post<SessionResumeResponse>(
        '/sessions/resume-session'
      );

      moduleLogger.debug('Session resumed', {
        sessionId: response.data.session_id,
        messageCount: response.data.messages?.length || 0,
      });
      return response.data;
    } catch (error) {
      moduleLogger.error('Failed to resume session', error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete (mark as deleted) a session from history
   */
  async deleteHistorySession(currentSessionId: string, targetSessionId: string): Promise<void> {
    try {
      moduleLogger.debug('Deleting session', { currentSessionId, targetSessionId });
      
      await this.apiClient.delete(`/sessions/${currentSessionId}/history/${targetSessionId}`);

      moduleLogger.debug('Session deleted successfully', { targetSessionId });

    } catch (error) {
      moduleLogger.error('Failed to delete session', error);
      throw this.handleError(error);
    }
  }

  /**
   * Setup axios interceptors for JWT authentication and error handling
   */
  private setupInterceptors(): void {
    // Request interceptor - automatically add JWT token
    this.apiClient.interceptors.request.use(
      async (config) => {
        const method = config.method?.toUpperCase() || 'UNKNOWN';
        const url = config.url || '';

        Object.assign(config.headers, getBrowserAuthHeaders(method));
        
        // Add JWT token if available
        if (this.tokenManager) {
          const token = this.tokenManager.getToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            moduleLogger.debug('Chat API request (authenticated)', { method, url });
          } else {
            moduleLogger.debug('Chat API request (no token)', { method, url });
          }
        } else {
          moduleLogger.debug('Chat API request (no auth)', { method, url });
        }
        
        return config;
      },
      (error) => {
        moduleLogger.error('Chat API request error', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle token refresh on 401
    this.apiClient.interceptors.response.use(
      (response) => {
        const method = response.config.method?.toUpperCase() || 'UNKNOWN';
        const url = response.config.url || '';
        const status = response.status;
        moduleLogger.debug('Chat API response successful', { method, url, status });
        return response;
      },
      async (error) => {
        const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
        const url = error.config?.url || '';
        const status = error.response?.status || 'NETWORK_ERROR';
        moduleLogger.error('Chat API error response', { method, url, status });

        const isCsrfValidationFailure = error.response?.status === 403
          && error.response?.data?.detail === 'CSRF validation failed';
        if (isCsrfValidationFailure && error.config && !error.config._csrfRetry) {
          error.config._csrfRetry = true;

          try {
            const refreshedCsrfToken = await refreshBrowserAuthSession();
            if (refreshedCsrfToken) {
              error.config.headers['X-CSRF-Token'] = refreshedCsrfToken;
              moduleLogger.info('Browser session refreshed after CSRF rejection; retrying request');
              return this.apiClient(error.config);
            }

            notifyBrowserAuthUnauthorized();
          } catch (refreshError) {
            moduleLogger.error('Browser session refresh failed after CSRF rejection', refreshError);
          }
        }
        
        // Handle 401 Unauthorized with token refresh
        if (error.response?.status === 401 && this.tokenManager && !error.config._retry) {
          moduleLogger.info('Token expired, attempting refresh');
          
          try {
            // Prevent multiple simultaneous refresh attempts
            if (!this.isRefreshingToken) {
              this.isRefreshingToken = true;
              this.refreshPromise = this.tokenManager.refreshToken();
            }
            
            const newToken = await this.refreshPromise;
            
            if (newToken) {
              moduleLogger.debug('Token refreshed successfully, retrying request');
              
              // Mark this request as a retry to prevent infinite loops
              error.config._retry = true;
              error.config.headers.Authorization = `Bearer ${newToken}`;
              
              // Reset refresh state
              this.isRefreshingToken = false;
              this.refreshPromise = null;
              
              // Retry the original request
              return this.apiClient(error.config);
            } else {
              moduleLogger.warn('Token refresh failed, clearing tokens');
              this.tokenManager.clearTokens();
            }
          } catch (refreshError) {
            moduleLogger.error('Token refresh error', refreshError);
            this.tokenManager.clearTokens();
          } finally {
            this.isRefreshingToken = false;
            this.refreshPromise = null;
          }
        }

        if (error.response?.status === 401) {
          notifyBrowserAuthUnauthorized();
        }
        
        return Promise.reject(error);
      }
    );
  }

  /**
   * Handle and normalize API errors with authentication awareness
   */
  private handleError(error: unknown): Error {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;

      // Use the centralized error categorizer to get error info
      // This enriches the error with categorization (session_expired, auth_failed, etc.)
      const errorInfo = categorizeError(axiosError);

      // Create custom error that preserves response data for error type detection
      const customError: any = new Error(errorInfo.message);
      customError.response = axiosError.response;
      customError.code = axiosError.code;
      customError.errorType = errorInfo.type;
      customError.isRecoverable = errorInfo.isRecoverable;
      return customError;
    }

    // Non-Axios errors
    if (error instanceof Error) {
      return error;
    }

    return new Error('An unexpected error occurred. Please try again.');
  }

  /**
   * Track a link click in the chat interface (non-blocking)
   * 
   * This method tracks when users click links in chat responses for analytics.
   * Failures are logged but don't interrupt the user experience.
   * 
   * @param sessionId - Current session identifier
   * @param linkData - Link metadata (url, text, is_external, optional message_id)
   */
  async trackLinkClick(
    sessionId: string,
    linkData: { 
      url: string; 
      text: string; 
      is_external: boolean;
      message_id?: number;
    }
  ): Promise<void> {
    try {
      await this.apiClient.post(`/sessions/${sessionId}/link-click`, {
        url: linkData.url,
        link_text: linkData.text,
        is_external: linkData.is_external,
        message_id: linkData.message_id
      });
      moduleLogger.debug('Link click tracked', { sessionId, url: linkData.url });
    } catch (error) {
      // Don't throw - tracking failures shouldn't break UX
      moduleLogger.error('Failed to track link click', error);
    }
  }
}

// Export singleton instance (without token manager initially)
export const chatService = new EnhancedChatService();

// Export class for testing and custom instantiation
export { EnhancedChatService };

// Export TokenManager interface for external implementations
export type { TokenManager };

// Default export for convenience
export default chatService;
